/**
 * Valve — K-based hydraulic resistance with isenthalpic throttling.
 * Supports time-varying opening for water hammer studies.
 *
 * N2 FIX (hydraulic honesty): the valve now EMITS a flow-dependent,
 * flow-SIGN-aware pressure drop instead of parking K in metadata where
 * nothing consumed it:
 *
 *     ΔP = −K·ρ·u·|u| / 2,   u = ṁ_through / (ρ·A)
 *     K  = K_fully_open / opening²
 *
 * The sign convention follows the network's outlet-cell application
 * (P_out_cell = P_junction + ΔP): forward flow (u > 0) ⇒ ΔP < 0 (drop
 * downstream); reverse flow (u < 0) ⇒ ΔP > 0 (back-pressure opposing the
 * reversal). The throughflow context (_throughMdot [signed kg/s], _inletRho,
 * _flowArea) is propagated by nsNetwork each iteration, mirroring the pump's
 * _inletRho/_inletMdot convention. WITHOUT that context (unit tests / direct
 * API use) the valve cannot know u and honestly returns ΔP = 0, preserving
 * the legacy standalone behaviour.
 *
 * FIXED-ΔP throttle mode (expansionValve mapping): when `fixedDeltaP` (Pa) is
 * set, the valve applies that drop against the current flow direction
 * (ΔP = −sign(u)·fixedDeltaP) instead of the K·ρu² law. This replaces the
 * old unit-nonsense `K = pressureDrop/100` derivation — a user-specified
 * design pressure drop is applied AS a pressure drop, in pascals.
 *
 * Throttling stays ISENTHALPIC (deltaH = 0): the Joule-Thomson temperature
 * change emerges from the IAPWS-97 property model at constant h.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';

/**
 * Fraction of the LOCAL absolute pressure a quadratic K-loss is allowed to
 * consume in one application (rail-guard, Fix Wave W1).
 *
 * The incompressible loss correlation ΔP = K·ρu²/2 extrapolated to a state
 * where ΔP ~ P_abs is outside its validity — the real device would cavitate /
 * choke long before. Un-capped, an oversized loss wrote outlet cells straight
 * onto the 611 Pa IAPWS floor (probe: a K=10, D=0.2 HX at 360 kg/s demanded
 * −658 kPa against a 400 kPa junction), producing phantom 0 °C states and
 * garbage flows. The cap keeps the emitted loss monotone-opposing and
 * bounded inside the physically meaningful envelope, in the same spirit as
 * the momentum solver's 50× two-phase friction cap.
 */
export const LOSS_CAP_FRACTION = 0.5;

/**
 * Cap a signed loss ΔP to LOSS_CAP_FRACTION of the available local absolute
 * pressure. Sign is preserved; no-op when P_available is absent/non-physical.
 */
export function capLossDeltaP(deltaP: number, P_available?: number): number {
  if (typeof P_available !== 'number' || !isFinite(P_available) || P_available <= 0) {
    return deltaP;
  }
  const cap = LOSS_CAP_FRACTION * P_available;
  if (Math.abs(deltaP) <= cap) return deltaP;
  return Math.sign(deltaP) * cap;
}

/**
 * Resolve the flow-dependent valve pressure drop from the solver-supplied
 * throughflow context. Shared by NSValve / NSControlValve / NSCheckValve
 * (and, formula-wise, the NSHeatExchanger K-loss).
 *
 * @param params      Component params bag (reads _throughMdot/_inletRho/_flowArea)
 * @param K           Loss coefficient (dimensionless, ≥ 0)
 * @param fixedDeltaP Optional fixed drop magnitude [Pa] — overrides the K law
 * @param P_available Optional local absolute pressure [Pa] for the rail-guard
 *                    cap (|ΔP| ≤ LOSS_CAP_FRACTION·P_available)
 * @returns ΔP [Pa] (sign-aware, 0 without flow context or at zero flow)
 */
export function valveDeltaP(
  params: Record<string, any>,
  K: number,
  fixedDeltaP?: number,
  P_available?: number,
): number {
  const mdot = params._throughMdot;
  if (typeof mdot !== 'number' || !isFinite(mdot) || mdot === 0) return 0;

  if (fixedDeltaP != null && isFinite(fixedDeltaP) && fixedDeltaP > 0) {
    // Fixed-ΔP throttle: user-specified drop opposing the flow direction.
    // Also rail-guarded — a specified drop larger than the available local
    // pressure would rail the IAPWS floor just the same.
    return capLossDeltaP(-Math.sign(mdot) * fixedDeltaP, P_available);
  }

  const rho = typeof params._inletRho === 'number' && params._inletRho > 0
    ? params._inletRho
    : 998; // liquid-water fallback when the upstream cell density is absent
  const A = typeof params._flowArea === 'number' && params._flowArea > 0
    ? params._flowArea
    : null;
  if (A === null || !(K > 0)) return 0;

  const u = mdot / (rho * A); // m/s, signed
  return capLossDeltaP(-K * rho * u * Math.abs(u) / 2, P_available); // Pa, opposes flow
}

export class NSValve implements NSComponent {
  id: string;
  type = 'valve';
  params: Record<string, any>;

  constructor(id: string, params: {
    Cv?: number;            // Flow coefficient (informational)
    opening?: number;       // 0 (closed) to 1 (fully open)
    K_fully_open?: number;  // Loss coefficient when fully open
    fixedDeltaP?: number;   // Pa — fixed throttle drop (expansionValve mode)
  }) {
    this.id = id;
    this.params = { Cv: 100, opening: 1.0, K_fully_open: 2.0, ...params };
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { opening, K_fully_open, fixedDeltaP } = this.params;

    // Loss coefficient increases as valve closes: K = K_open / opening²
    const eff_opening = Math.max(0.001, opening); // Prevent division by zero
    const K = K_fully_open / (eff_opening * eff_opening);

    // N2: flow-dependent ΔP from the live throughflow context (0 when the
    // context is unavailable — standalone/unit-test path unchanged).
    // inlet.P feeds the rail-guard cap (|ΔP| ≤ LOSS_CAP_FRACTION·P_inlet).
    const deltaP = valveDeltaP(this.params, K, fixedDeltaP, inlet?.P);

    return {
      deltaP,
      deltaH: 0,  // Isenthalpic
      momentumSource: 0,
      energySource: 0,
      metadata: { K, opening: eff_opening, deltaP_valve: deltaP },
    };
  }

  /** Set valve opening (for transient studies). */
  setOpening(opening: number): void {
    this.params.opening = Math.max(0, Math.min(1, opening));
  }
}
